import { useState } from 'react';
import type { Device } from '@glyph/protocol-ts';
import { useApi, useCommand, useDevices, useMe, useSettings } from '@glyph/core';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, Dialog, EmptyState, Logo, Note, PageHeader, SegmentedControl, StatusPill, useToast } from '@glyph/ui';

type Place = 'top' | 'bottom';
const PLACES: { value: Place; label: string }[] = [
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
];
/** Font size steps for a room operator: big enough to matter on a screen, never unreadable or absurd. */
const STEP = 4;
const MIN_PX = 24;
const MAX_PX = 160;

/** One room: where the captions sit and how big they are. Nothing else. */
function RoomCard({ unit }: { unit: Device }) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const settings = useSettings(unit.id);
  const cmd = useCommand(unit.id);
  const [busy, setBusy] = useState(false);
  const [power, setPower] = useState<null | 'reboot' | 'shutdown'>(null);
  const d = settings.data?.display;
  const place: Place = d?.v_align === 'top' ? 'top' : 'bottom';
  const size = d?.font_size_px ?? 0;
  const send = async (patch: { v_align?: Place; font_size_px?: number }, what: string) => {
    setBusy(true);
    try {
      await api.patchSettings(unit.id, { display: patch });
      await qc.invalidateQueries({ queryKey: ['settings', unit.id] });
      toast.push(`${unit.name}: ${what}`, 'ok');
    } catch (e) {
      toast.push((e as Error).message || 'Could not change the captions', 'warn');
    } finally {
      setBusy(false);
    }
  };
  const doPower = async () => {
    if (!power) return;
    const r = await cmd.run(power === 'reboot' ? 'system.reboot' : 'system.shutdown', power === 'reboot' ? { confirm: true } : {});
    if (r) toast.push(power === 'reboot' ? `${unit.name} is restarting · captions back in about a minute` : `${unit.name} is powering off`, 'ok');
    else toast.push(cmd.lastError?.message || 'The unit did not answer', 'warn');
    setPower(null);
  };
  const resize = (delta: number) => {
    const next = Math.min(MAX_PX, Math.max(MIN_PX, size + delta));
    if (next !== size) void send({ font_size_px: next }, `text ${delta > 0 ? 'larger' : 'smaller'}`);
  };
  return (
    <Card>
      <CardHeader
        title={unit.name}
        meta={unit.assignment?.event_name ?? ''}
        actions={<StatusPill tone={unit.online ? (unit.state === 'live' ? 'ok' : 'fg4') : 'warn'}>{unit.online ? (unit.state === 'live' ? 'CAPTIONING' : 'STANDBY') : 'OFFLINE'}</StatusPill>}
      />
      <div className="flex flex-col gap-5 border-t border-line px-4 py-5">
        <div className="flex flex-col gap-2">
          <span className="micro text-fg3">Captions at the</span>
          <SegmentedControl
            fullWidth
            value={place}
            options={PLACES}
            onChange={(v) => void send({ v_align: v }, `captions at the ${v}`)}
            aria-label={`Caption placement for ${unit.name}`}
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="micro text-fg3">Text size</span>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <Button size="responsive" disabled={busy || !d || size <= MIN_PX} onClick={() => resize(-STEP)} aria-label={`Smaller text on ${unit.name}`}>
              A−
            </Button>
            <span className="min-w-[4.5em] text-center font-mono text-lg tabular" aria-label={`Text size on ${unit.name}`}>
              {d ? `${size} px` : '—'}
            </span>
            <Button size="responsive" disabled={busy || !d || size >= MAX_PX} onClick={() => resize(STEP)} aria-label={`Larger text on ${unit.name}`}>
              A+
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className="micro text-fg3">Power</span>
          <div className="grid grid-cols-2 gap-3">
            <Button size="responsive" disabled={!unit.online} onClick={() => setPower('reboot')} aria-label={`Restart ${unit.name}`}>
              Restart
            </Button>
            <Button size="responsive" variant="destructive-outline" disabled={!unit.online} onClick={() => setPower('shutdown')} aria-label={`Power off ${unit.name}`}>
              Power off
            </Button>
          </div>
        </div>
        {!unit.online && <Note tone="warn">This room is offline: a placement or size change reaches it when it reconnects.</Note>}
      </div>
      <Dialog
        open={!!power}
        onClose={() => setPower(null)}
        title={power === 'reboot' ? `Restart ${unit.name}?` : `Power off ${unit.name}?`}
        meta={power === 'reboot' ? 'CAPTIONS STOP FOR ABOUT A MINUTE' : 'CAPTIONS STOP UNTIL SOMEONE TURNS IT BACK ON'}
        confirm={{ label: power === 'reboot' ? 'Restart' : 'Power off', destructive: true, busy: !!cmd.pending['system.reboot'] || !!cmd.pending['system.shutdown'], onClick: doPower }}
      >
        {power === 'reboot'
          ? 'The unit restarts and comes back on its own with the same settings.'
          : 'The unit shuts down. To start it again, unplug its power and plug it back in.'}
      </Dialog>
    </Card>
  );
}

/** The whole console for a room operator, and its home page: each assigned room with Top / Bottom and text size. */
export function RoomOperatorScreen({ onSignOut }: { onSignOut: () => void }) {
  const me = useMe();
  const devices = useDevices();
  const units = (devices.data ?? []).filter((d) => d.status !== 'pending');
  return (
    <div className="min-h-screen bg-bg text-fg">
      <div className="mx-auto flex max-w-[560px] flex-col gap-4 px-4 py-6">
        <div className="flex items-center justify-between">
          <Logo />
          <Button size="sm" variant="ghost" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
        <PageHeader title={units.length === 1 ? units[0]!.name : 'Your rooms'} meta={`${me.data?.name ?? ''} · ROOM OPERATOR`} />
        {devices.isSuccess && units.length === 0 && <EmptyState label="No rooms assigned yet: ask your admin" />}
        {units.map((u) => (
          <RoomCard key={u.id} unit={u} />
        ))}
      </div>
    </div>
  );
}
